import { useState } from 'react';
import { DropdownMenuCheckboxItemProps } from '@radix-ui/react-dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@curtin-dxp/curtin-ui';
import { useElementSize } from '@/hooks';
import { useAwmsStore } from '@/store';
import { cn } from '@/lib/utils';
import { ChevronIcon } from '@/components';
import { Option, FilterOption } from '../DataTable';
import { FILTERS } from '@/constants';
import { SearchFilter, SearchFilterState } from '@/store/types';

export const SearchFilterDropDown = ({
  placeholder,
  checkbox,
  options,
  stateFilterFieldName,
  stateFilterValue,
  selectedState,
  setSelectedState,
  className,
}: FilterOption) => {
  const [open, setOpen] = useState<boolean>(false);
  const [triggerRef, { width: triggerWidth }] = useElementSize<HTMLButtonElement>();
  const setSearchFilters = useAwmsStore((state) => state.setSearchFilters);
  const searchFilters = useAwmsStore((state) => state.searchFilters);
  const setSelectedSchoolId = useAwmsStore((state) => state.setSelectedSchoolId);
  const setSelectedStudyPeriodId = useAwmsStore((state) => state.setSelectedStudyPeriodId);
  const setSelectedDisciplineId = useAwmsStore((state) => state.setSelectedDisciplineId);
  const setSelectedEmpStatusAbbr = useAwmsStore((state) => state.setSelectedEmpStatusAbbr);

  const handleCheckboxChange = (option: SearchFilter) => {
    if (Array.isArray(selectedState)) {
      const exists = selectedState.some((item) => item.value === option.value);
      const updatedValues = exists
        ? selectedState.filter((item) => item.value !== option.value)
        : [...selectedState, option];
      setSelectedState(updatedValues);
      setSearchFilters({ ...searchFilters, [FILTERS.EMPSTATUSABBR]: updatedValues });
    }
  };

  const onClickHandler = (option: Option) => {
    if (option.value === selectedState) return;

    const updatedFilters: SearchFilterState = {
      ...searchFilters,
      [stateFilterFieldName]: option,
    };

    setSearchFilters(updatedFilters);
    setSelectedState(option.value);

    if (stateFilterFieldName === FILTERS.SCHOOL) {
      // Reset Study Period, Discipline and Employment Status when School changes
      setSearchFilters({
        [FILTERS.STUDYPERIOD]: undefined,
        [FILTERS.DISCIPLINE]: undefined,
        [FILTERS.EMPSTATUSABBR]: undefined,
      });
      setSelectedStudyPeriodId(0);
      setSelectedDisciplineId(0);
      setSelectedEmpStatusAbbr([]);
    } else if (stateFilterFieldName === FILTERS.YEAR) {
      // Reset School, Study Period, Discipline and Employment Status when Year changes
      setSearchFilters({
        [FILTERS.SCHOOL]: undefined,
        [FILTERS.STUDYPERIOD]: undefined,
        [FILTERS.DISCIPLINE]: undefined,
      });
      setSelectedSchoolId(0);
      setSelectedStudyPeriodId(0);
      setSelectedDisciplineId(0);
      setSelectedEmpStatusAbbr([]);
    }

    setOpen(false);
  };

  const getFilterPlaceholder = (value: Option | undefined, placeholder: string): string => {
    if (!value || checkbox) {
      return placeholder;
    }

    return value.label;
  };

  const isChecked = (selectedState: number | SearchFilter[], option: SearchFilter): boolean => {
    if (Array.isArray(selectedState)) {
      return selectedState.some((item) => item.value === option.value);
    }
    return selectedState === option.value;
  };

  return (
    <DropdownMenu open={open}>
      <DropdownMenuTrigger asChild ref={triggerRef} className="focus-visible:outline-none" tabIndex={-1}>
        <div className="inline-flex items-center">
          <Button
            variant="outline"
            className={cn(
              `h-10 w-[360px] justify-between rounded-lg border-[1px] border-input bg-white px-3 py-5 text-base font-normal leading-6
              text-gray-600 hover:border-deep-blue focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-deep-blue
              ${open ? 'ring-1 ring-deep-blue' : ''}`,
              className,
            )}
            aria-label={placeholder}
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : 'false'}
            onClick={() => setOpen(!open)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setOpen(!open);
              }
            }}
          >
            <span className="w-full truncate text-start" title={getFilterPlaceholder(stateFilterValue, placeholder)}>
              {getFilterPlaceholder(stateFilterValue, placeholder)}
            </span>
            <ChevronIcon
              className="h-4 w-4 fill-[#000000] transition-transform duration-300"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={`scrollbar-w-4 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar scrollbar-track-[#303030]
          scrollbar-thumb-[#717171] z-[300] max-h-[calc(40vh)] overflow-y-auto rounded-2xl border-none bg-white shadow-xl`}
        style={{ width: `${triggerWidth}px` }}
        onInteractOutside={() => setOpen(false)}
        onEscapeKeyDown={() => setOpen(false)}
      >
        {!checkbox && (
          <>
            {options.map((option, index) => (
              <DropdownMenuItem
                key={index}
                className={`cursor-pointer py-[10px] font-semibold hover:text-deep-blue focus:text-deep-blue
                ${option.value === selectedState ? 'text-deep-blue' : ''}`}
                onClick={() => {
                  onClickHandler(option);
                }}
              >
                <span className="w-full truncate text-start" title={option.label}>
                  {option.label}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
        {checkbox && (
          <>
            {options.map((option, index) => (
              <DropdownMenuCheckboxItem
                key={index}
                checked={isChecked(selectedState, option)}
                className={`cursor-pointer py-[10px] font-semibold hover:text-deep-blue focus:text-deep-blue
                ${isChecked(selectedState, option) ? 'text-deep-blue' : ''}`}
                onCheckedChange={() => handleCheckboxChange(option)}
              >
                <span className="w-full truncate text-start" title={option.label}>
                  {option.label}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
